import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import {
  Container, PageLayout, Headline, Card, Button, Input, Label, MutedText,
} from "../components/ui";
import { PinPad } from "../components/PinPad";
import { useChildren } from "../lib/childrenStore";
import { useAuth } from "../lib/auth";
import { useParentGate } from "../lib/parentGate";
import { getMyStories, setKidPin } from "../lib/dataSource";
import { PairingPanel } from "../components/PairingPanel";
import { InstallAppCard } from "../components/InstallAppCard";
import { LegalFooter } from "../components/LegalFooter";
import { isPlus } from "../lib/plan";
import { readAdminViewPref, writeAdminViewPref } from "../lib/adminView";
import { formatPlayerLog, getPlayerLog } from "../lib/playerLog";
import { useChildSheetThumbnail } from "../lib/childSheetThumbnail";
import type { Child, ChildInput, StoryCard } from "../lib/types";

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? "0.1.0";

/**
 * Discreet disclosure for the player's diagnostics ring buffer (see
 * src/lib/playerLog.ts). None of the iOS lock-screen playback bugs it
 * exists for reproduce with a debugger attached — the screen has to
 * actually lock — so this is the only way to get the sequence of events
 * off a device after the owner reproduces one: open it, tap Copier, paste.
 * Deliberately unstyled/small and tucked next to the app version rather
 * than presented as a feature.
 */
function PlayerLogPanel() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Re-read on open rather than subscribing: this is a debugging tool, not
  // a live view, and the buffer only changes from player events that have
  // nothing to do with the Settings page being open.
  const [entries, setEntries] = useState(() => getPlayerLog());

  return (
    <details
      className="mt-3 text-violet-muted text-xs"
      onToggle={(e) => {
        if ((e.target as HTMLDetailsElement).open) setEntries(getPlayerLog());
      }}
    >
      <summary className="cursor-pointer select-none normal-case tracking-normal">
        {t("settings.playerLog.title")}
      </summary>
      <div className="mt-2 flex flex-col items-start gap-2 normal-case tracking-normal">
        {entries.length === 0 ? (
          <p>{t("settings.playerLog.empty")}</p>
        ) : (
          <pre className="max-h-40 w-full overflow-y-auto whitespace-pre-wrap break-words rounded bg-surface/50 p-2 text-left text-[10px] leading-tight">
            {formatPlayerLog()}
          </pre>
        )}
        <button
          type="button"
          className="underline"
          onClick={() => {
            void (async () => {
              try {
                await navigator.clipboard.writeText(formatPlayerLog());
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              } catch {
                /* clipboard unavailable — nothing to fall back to here */
              }
            })();
          }}
        >
          {copied ? t("settings.playerLog.copied") : t("settings.playerLog.copy")}
        </button>
      </div>
    </details>
  );
}

function ChildForm({
  initial, askParentalAttestation = false, onSubmit, onCancel,
}: {
  initial?: Child;
  /** Adding a child only: a required « je suis le parent ou le représentant
   *  légal » checkbox, without which « Enregistrer » stays disabled. */
  askParentalAttestation?: boolean;
  onSubmit: (input: ChildInput) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(initial?.first_name ?? "");
  const [age, setAge] = useState(initial?.age ?? 6);
  const [likes, setLikes] = useState(initial?.likes ?? "");
  const [companion, setCompanion] = useState(initial?.companion ?? "");
  const [saving, setSaving] = useState(false);
  const [attested, setAttested] = useState(false);

  return (
    <Card className="flex flex-col gap-3">
      <div>
        <Label>{t("settings.childName")}</Label>
        <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div>
        <Label>{t("settings.childAge")}</Label>
        <Input type="number" min={3} max={12} value={age} onChange={(e) => setAge(Number(e.target.value))} />
      </div>
      <div>
        <Label>{t("settings.childLikes")}</Label>
        <Input value={likes} onChange={(e) => setLikes(e.target.value)} />
      </div>
      <div>
        <Label>{t("settings.childCompanion")}</Label>
        <Input value={companion} onChange={(e) => setCompanion(e.target.value)} />
      </div>
      {askParentalAttestation && (
        <label className="flex items-start gap-2.5 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
            className="mt-0.5 accent-sun"
          />
          <span className="flex-1">{t("settings.parentalAttestation")}</span>
        </label>
      )}
      <div className="flex gap-2">
        <Button
          variant="secondary"
          className="flex-1"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </Button>
        <Button
          className="flex-1"
          disabled={!firstName || saving || (askParentalAttestation && !attested)}
          onClick={async () => {
            setSaving(true);
            try {
              await onSubmit({ first_name: firstName, age, likes, companion });
            } finally {
              setSaving(false);
            }
          }}
        >
          {t("common.save")}
        </Button>
      </div>
    </Card>
  );
}

/** Vignette du personnage choisi : le portrait carré déjà recadré par le
 *  serveur (voir `useChildSheetThumbnail`), avec la planche complète et le
 *  recadrage CSS fixe (`frontPoseCrop`) en repli si ce nouvel endpoint
 *  échoue (backend pas encore déployé). L'image est privée, chargée avec le
 *  jeton, jamais par une URL publique. */
function SheetThumb({ childId, sheetId, name }: { childId: string; sheetId: string; name: string }) {
  const { t } = useTranslation();
  const { url, crop } = useChildSheetThumbnail(childId, sheetId);
  return (
    <div className="relative w-16 h-16 shrink-0 rounded-card bg-night border border-line overflow-hidden">
      {url && (
        <img
          src={url}
          alt={t("appearance.sheetAlt", { name })}
          className={crop ? "absolute max-w-none h-auto" : "absolute inset-0 w-full h-full object-cover"}
          style={crop ?? undefined}
        />
      )}
    </div>
  );
}

/**
 * Carte d'un enfant dans Réglages : prénom, âge, goûts, personnage choisi,
 * lien vers « L'apparence de <prénom> », et suppression confirmée en ligne
 * (supprimer l'enfant supprime aussi sa description et ses personnages,
 * plan D3 : autant le dire avant).
 */
function ChildCard({
  child, onEdit, onRemove,
}: {
  child: Child;
  onEdit: () => void;
  onRemove: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const appearanceLabel = child.sheet
    ? child.sheet.matches_appearance
      ? t("appearance.settingsLinkChosen")
      : t("appearance.settingsLinkStale")
    : child.appearance
      ? t("appearance.settingsLinkPending")
      : t("appearance.settingsLinkEmpty");

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {child.sheet && <SheetThumb childId={child.id} sheetId={child.sheet.id} name={child.first_name} />}
          <div className="min-w-0">
            <p className="font-heading font-semibold text-sm">{child.first_name}, {child.age} ans</p>
            <MutedText>{child.likes}</MutedText>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" className="tap-target text-violet-soft" onClick={onEdit} aria-label={t("settings.editChild")}>
            <Pencil size={18} />
          </button>
          <button type="button" className="tap-target text-coral" onClick={() => setConfirming(true)} aria-label={t("settings.removeChild")}>
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      <Link
        to={`/settings/children/${child.id}/appearance`}
        className="flex items-center gap-2 font-heading font-semibold text-sm text-sun"
      >
        <Sparkles size={16} aria-hidden />
        <span className="flex-1">{appearanceLabel}</span>
        <ChevronRight size={16} className="text-violet-soft" aria-hidden />
      </Link>
      {confirming && (
        <div className="flex flex-col gap-3 border-t border-line pt-3" role="alertdialog" aria-label={t("settings.removeChild")}>
          <p className="text-sm text-paper">{t("settings.removeChildConfirm", { name: child.first_name })}</p>
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setConfirming(false)} disabled={removing}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="coral"
              className="flex-1"
              disabled={removing}
              onClick={async () => {
                setRemoving(true);
                try {
                  await onRemove();
                } finally {
                  setRemoving(false);
                }
              }}
            >
              {t("common.delete")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Calque plein écran de définition du code PIN, en deux temps (on ne fait
 * pas confiance à la première frappe sur un pavé tactile sans retour visuel
 * du chiffre saisi — une confirmation attrape la faute de frappe avant
 * qu'elle ne devienne le code qui bloque l'enfant hors du mode enfant).
 * Un seul PinPad reste monté d'un bout à l'autre : c'est ce qui motive le
 * vidage du buffer sur succès dans PinPad lui-même (cf. son commentaire).
 */
function KidPinSetup({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const { refreshMe } = useAuth();
  const [step, setStep] = useState<"choose" | "confirm">("choose");
  const [firstPin, setFirstPin] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialogRef.current?.focus();
    // Le calque est plein écran mais reste un enfant du flux normal de la
    // page : sans ceci, la page derrière lui continue de défiler sous les
    // doigts (le scroll du body n'a pas conscience du calque par-dessus).
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleChoose(pin: string): Promise<boolean> {
    setFirstPin(pin);
    setErrorMessage(null);
    setStep("confirm");
    return true;
  }

  async function handleConfirm(pin: string): Promise<boolean> {
    if (pin !== firstPin) {
      setErrorMessage(t("settings.pinMismatch"));
      setStep("choose");
      setFirstPin("");
      return false;
    }
    try {
      await setKidPin(pin);
      await refreshMe();
      onSaved();
      onClose();
      return true;
    } catch {
      setErrorMessage(t("settings.pinSaveError"));
      return false;
    }
  }

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={t("settings.pinDialogLabel")}
      className="safe-top fixed inset-0 z-50 bg-night text-paper flex flex-col items-center justify-center gap-4 px-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))]"
      style={{ "--safe-top-extra": "2rem" } as React.CSSProperties}
    >
      <PinPad
        title={step === "choose" ? t("settings.pinChooseTitle") : t("settings.pinConfirmTitle")}
        subtitle={step === "choose" ? t("settings.pinChooseSubtitle") : t("settings.pinConfirmSubtitle")}
        onSubmit={step === "choose" ? handleChoose : handleConfirm}
        errorMessage={errorMessage ?? undefined}
      />
      <Button variant="ghost" onClick={onClose}>
        {t("common.cancel")}
      </Button>
    </div>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { children, addChild, updateChild, removeChild } = useChildren();
  const { me, logout } = useAuth();
  const { unlock: unlockParent } = useParentGate();
  const plus = isPlus(me);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const [pinSaved, setPinSaved] = useState(false);
  // Tout vient du serveur, jamais recalculé côté client (D8) : la période
  // Chouette+ est ancrée sur la date de souscription, pas sur le mois
  // civil. `null` = pas d'abonnement, ou prix non résolvable côté serveur —
  // on n'affiche alors rien plutôt qu'un compteur trompeur.
  const storiesUsedThisPeriod = me?.stories_used_this_period ?? null;
  const storiesPerPeriod = me?.stories_per_period ?? null;
  const periodStart = me?.period_start ?? null;
  const periodEnd = me?.period_end ?? null;
  const periodDateFormat = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long" });
  const [adminView, setAdminView] = useState(readAdminViewPref);
  // Sert uniquement au compteur de l'entrée « Mes histoires » (la liste vit
  // sur /settings/stories). `null` = chargement ou échec : dans les deux cas
  // l'entrée reste cachée plutôt que d'afficher une erreur pour un détail.
  const [myStories, setMyStories] = useState<StoryCard[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyStories()
      .then((list) => {
        if (!cancelled) setMyStories(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageLayout>
      <Container className="pt-8 flex flex-col gap-6">
        <Headline>{t("settings.title")}</Headline>

        <section>
          <h2 className="font-heading font-bold text-lg mb-3">{t("settings.children")}</h2>
          <div className="flex flex-col gap-3">
            {children.map((c) =>
              editingId === c.id ? (
                <ChildForm
                  key={c.id}
                  initial={c}
                  onCancel={() => setEditingId(null)}
                  onSubmit={async (input) => {
                    await updateChild(c.id, input);
                    setEditingId(null);
                  }}
                />
              ) : (
                <ChildCard
                  key={c.id}
                  child={c}
                  onEdit={() => setEditingId(c.id)}
                  onRemove={() => removeChild(c.id)}
                />
              ),
            )}
            {editingId === "new" ? (
              <ChildForm
                askParentalAttestation
                onCancel={() => setEditingId(null)}
                onSubmit={async (input) => {
                  // The form only submits once the attestation is ticked.
                  const child = await addChild({ ...input, parental_attestation: true });
                  setEditingId(null);
                  // Proposé juste après la création, avec « Plus tard » :
                  // facultatif, le profil marche sans (plan D8).
                  navigate(`/settings/children/${child.id}/appearance?new=1`);
                }}
              />
            ) : (
              // `secondary` seul lisait comme une carte enfant de plus (même
              // fond, même bordure pleine) — repéré sur la page Réglages.
              // Fond transparent, bordure pointillée dans le jaune : l'action
              // « en ajouter un » se distingue d'un coup d'œil des cartes
              // qu'elle complète, sans changer son comportement.
              <Button
                variant="secondary"
                onClick={() => setEditingId("new")}
                className="w-full !bg-transparent !border-2 !border-dashed !border-sun/60 !text-sun !shadow-none"
              >
                <Plus size={16} />
                {t("settings.addChild")}
              </Button>
            )}
          </div>
        </section>

        {myStories && myStories.length > 0 && (
          <section>
            <Link
              to="/settings/stories"
              className="block bg-surface border border-line rounded-card overprint ink-line p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-heading font-bold text-lg">{t("settings.myStories")}</p>
                  <MutedText className="mt-0.5">{t("settings.myStoriesCount", { count: myStories.length })}</MutedText>
                </div>
                <ChevronRight size={20} className="shrink-0 text-violet-soft" aria-hidden />
              </div>
            </Link>
          </section>
        )}

        <section>
          <h2 className="font-heading font-bold text-lg mb-3">{t("settings.parentPin")}</h2>
          <Card className="flex flex-col gap-3">
            <MutedText>{plus ? t("settings.parentPinHintPlus") : t("settings.parentPinHint")}</MutedText>
            <MutedText>{me?.kid_pin_set ? t("settings.parentPinSet") : t("settings.parentPinNotSet")}</MutedText>
            <Button
              onClick={() => {
                setPinSaved(false);
                setPinSetupOpen(true);
              }}
            >
              {me?.kid_pin_set ? t("settings.changePin") : t("settings.setPin")}
            </Button>
            {pinSaved && <p className="text-mint text-sm font-heading font-semibold">{t("common.save")} ✓</p>}
          </Card>
        </section>

        <section>
          <h2 className="font-heading font-bold text-lg mb-3">{t("settings.subscription")}</h2>
          <Link
            to="/plus"
            className="block bg-surface border border-line rounded-card overprint ink-line p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-heading font-bold text-lg text-sun">
                  {plus ? t("settings.planPlus") : t("settings.planFree")}
                </p>
                <MutedText className="mt-0.5">
                  {plus ? t("settings.planPlusHint") : t("settings.planFreeHint")}
                </MutedText>
              </div>
              <ChevronRight size={20} className="shrink-0 text-violet-soft" aria-hidden />
            </div>
            <p className="mt-3 font-heading font-semibold text-sm text-paper">{t("settings.seePlus")}</p>
          </Link>
          {/* Ce qui reste se dit en histoires, jamais en crédits — règle
              d'affichage de memoneo (billing-plans-and-credits.md §3.1).
              Pour un compte gratuit, il n'y a plus rien à compter (D6) : la
              carte ci-dessus (catalogue + bouton Chouette+) suffit. */}
          {plus && storiesUsedThisPeriod !== null && storiesPerPeriod !== null && periodStart && periodEnd && (
            <MutedText className="mt-2 block">
              {t("settings.storiesUsedThisPeriod", {
                used: storiesUsedThisPeriod,
                total: storiesPerPeriod,
                start: periodDateFormat.format(new Date(periodStart)),
                end: periodDateFormat.format(new Date(periodEnd)),
              })}
            </MutedText>
          )}
        </section>

        {me?.is_admin && (
          <Card className="flex items-start justify-between gap-4">
            <div>
              <p className="font-heading font-bold text-sm">{t("settings.adminView")}</p>
              <MutedText className="mt-0.5">{t("settings.adminViewHint")}</MutedText>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={adminView}
              aria-label={t("settings.adminView")}
              onClick={() => {
                writeAdminViewPref(!adminView);
                setAdminView(!adminView);
              }}
              className="tap-target shrink-0 inline-flex items-center justify-center"
            >
              {/* The button stays a 44px tap target; the track inside is the visual. */}
              <span
                aria-hidden
                className={`relative block h-7 w-12 rounded-pill border border-line transition-colors ${adminView ? "bg-sun" : "bg-surface"}`}
              >
                <span
                  className={`absolute top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-paper transition-all ${adminView ? "left-6" : "left-0.5"}`}
                />
              </span>
            </button>
          </Card>
        )}

        <InstallAppCard />

        <PairingPanel />

        {plus && (
          <Button variant="coral" onClick={() => navigate("/kid")}>
            {t("settings.kidModeLaunch")}
          </Button>
        )}

        <Button
          variant="ghost"
          onClick={() => {
            logout();
            navigate("/login");
          }}
        >
          {t("settings.logout")}
        </Button>

        <div className="text-center text-violet-muted text-xs font-heading uppercase tracking-wide pt-4">
          <p>{t("settings.signature")}</p>
          <p className="mt-1 normal-case tracking-normal">{t("settings.version", { version: APP_VERSION })}</p>
          <PlayerLogPanel />
        </div>

        <LegalFooter />
      </Container>
      {pinSetupOpen && (
        <KidPinSetup
          onClose={() => setPinSetupOpen(false)}
          onSaved={() => {
            setPinSaved(true);
            // Il vient de choisir le code : ne pas l'enfermer dehors aussitôt.
            unlockParent();
          }}
        />
      )}
    </PageLayout>
  );
}
